import React from 'react';
import { FileText, Mail, Phone, MapPin } from 'lucide-react';
import { company } from '../data/mockData';
import SEO from '../components/SEO';

const TermsOfService = () => {
  const sections = [
    {
      title: '1. Acceptance of Terms',
      body: 'By accessing or using our website, submitting inquiries, or engaging with Stallion Bulktech for products and services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any part of these Terms, please discontinue use of our website and services.'
    },
    {
      title: '2. About Us',
      body: 'Stallion Bulktech is a manufacturer and execution partner specializing in Industrial Machine Guards, Dust Suppression Systems, and Wear Resistance solutions engineered for reliability, reduced maintenance, and long operational life.'
    },
    {
      title: '3. Use of Website',
      intro: 'You agree to use our website only for lawful purposes. You shall not:',
      items: [
        'Attempt to gain unauthorized access to any portion of the website, servers, or databases',
        'Introduce viruses, malware, or any harmful code',
        'Use automated systems (bots, scrapers) to extract content without written permission',
        'Interfere with the normal operation of the website or its security features',
        'Misrepresent your identity or submit false inquiries'
      ]
    },
    {
      title: '4. Intellectual Property Rights',
      body: 'All content on this website — including text, graphics, logos, product images, drawings, technical documentation, videos, and design layouts — is the property of Stallion Bulktech or its licensors and is protected by applicable copyright and trademark laws. No content may be reproduced, distributed, or used for commercial purposes without prior written consent.'
    },
    {
      title: '5. Product Information and Specifications',
      body: 'We make reasonable efforts to ensure that product descriptions, technical specifications, and images are accurate. However, actual products may vary based on site conditions, customization, and engineering requirements. Final specifications are confirmed through formal quotations, drawings, and project agreements.'
    },
    {
      title: '6. Quotations, Orders and Pricing',
      intro: 'All quotations issued by Stallion Bulktech are:',
      items: [
        'Valid for the period specified in the quotation document',
        'Subject to confirmation at the time of order placement',
        'Based on the scope, materials, and site data shared at the time of inquiry'
      ],
      outro: 'Prices are exclusive of applicable taxes, freight, insurance, and installation charges unless stated otherwise. Orders are confirmed only upon receipt of a formal purchase order and acceptance by Stallion Bulktech.'
    },
    {
      title: '7. Payment Terms',
      body: 'Payment terms are specified in the quotation or purchase order. Invoices must be settled within the agreed credit period. Late payments may attract interest, and delivery or execution may be withheld in case of outstanding dues.'
    },
    {
      title: '8. Delivery, Installation and Execution',
      body: 'Delivery and execution timelines are best estimates based on the agreed scope and site readiness. Stallion Bulktech shall not be held responsible for delays caused by factors outside our control, including but not limited to site-readiness issues, material shortages, transport disruptions, or force majeure events.'
    },
    {
      title: '9. Warranty',
      body: 'Our products carry a warranty as specified in the quotation or sales contract. Warranty covers manufacturing defects under normal operating conditions and does not extend to damage caused by misuse, improper installation by third parties, unauthorized modifications, or wear and tear beyond the expected service life.'
    },
    {
      title: '10. Limitation of Liability',
      intro: 'To the fullest extent permitted by law, Stallion Bulktech shall not be liable for:',
      items: [
        'Indirect, incidental, or consequential damages',
        'Loss of production, revenue, profits, or business opportunities',
        'Damages arising out of misuse or unauthorized alteration of our products',
        'Errors or omissions in website content'
      ],
      outro: 'Our total liability in any matter shall not exceed the value of the specific order or contract in question.'
    },
    {
      title: '11. Third-Party Links and Tools',
      body: 'Our website may contain links to third-party websites, collaborators, or tools. We are not responsible for the content, policies, or practices of such third parties. Access and use of those resources is at your own risk.'
    },
    {
      title: '12. User Submissions and Inquiries',
      body: 'Any information, drawings, or data you share with us through forms, emails, or meetings will be used for the purpose of understanding your requirements and providing solutions. You confirm that you have the right to share such information and that it does not infringe upon the rights of any third party.'
    },
    {
      title: '13. Confidentiality',
      body: 'We treat project-specific information shared during the inquiry, design, and execution stages as confidential. Likewise, we expect clients and partners to maintain confidentiality of proprietary designs, engineering details, and pricing shared by us.'
    },
    {
      title: '14. Force Majeure',
      body: 'Stallion Bulktech shall not be held liable for any failure or delay in performance caused by events beyond our reasonable control, including natural disasters, pandemics, strikes, government restrictions, transport disruptions, or failure of utilities.'
    },
    {
      title: '15. Termination',
      body: 'We reserve the right to suspend or terminate access to our website, services, or ongoing engagements in the event of breach of these Terms, non-payment, misuse of our products or brand, or any conduct that adversely affects our operations or reputation.'
    },
    {
      title: '16. Indemnification',
      body: 'You agree to indemnify and hold Stallion Bulktech, its directors, employees, and partners harmless from any claims, damages, liabilities, or expenses arising from your misuse of our website, products, services, or violation of these Terms.'
    },
    {
      title: '17. Governing Law and Jurisdiction',
      body: 'These Terms shall be governed by and interpreted in accordance with the laws of India. Any disputes arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the competent courts located in our registered office jurisdiction.'
    },
    {
      title: '18. Dispute Resolution',
      body: 'In the event of a dispute, both parties agree to first attempt resolution through good-faith discussions. If the matter remains unresolved, it may be referred to arbitration or the appropriate legal forum as per applicable Indian law.'
    },
    {
      title: '19. Changes to These Terms',
      body: 'We may revise these Terms of Service from time to time to reflect changes in our operations, services, or legal requirements. Updated versions will be posted on this page with a revised Effective Date. Continued use of our website or services after such updates constitutes acceptance of the revised Terms.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-[#f9fafb]">
      <SEO
        title="Terms of Service | Stallion Bulktech"
        description="Review the Stallion Bulktech Terms of Service governing access to and use of our website, products, and services including quotations, warranty, liability, and dispute resolution."
        path="/terms-of-service"
      />
      {/* Hero */}
      <section className="relative pt-24 pb-10 md:pt-32 md:pb-14 overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-20 left-1/4 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-50 border border-cyan-200 rounded-full mb-6">
            <FileText className="h-4 w-4 text-cyan-600" />
            <span className="text-cyan-600 text-sm font-medium">Legal</span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="terms-of-service-heading">
            Terms of Service
          </h1>
          <p className="text-sm md:text-base text-gray-500">
            Effective Date: 15 November 2018
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="pb-20 md:pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/90 backdrop-blur-xl border border-gray-200 rounded-2xl p-6 sm:p-10 md:p-12 shadow-xl">
            <p className="text-base md:text-lg text-gray-700 leading-relaxed mb-10">
              Welcome to Stallion Bulktech. These Terms of Service ("Terms") govern your access to and use of our website, products, and services. By engaging with us, you agree to these Terms. Please read them carefully.
            </p>

            <div className="space-y-10">
              {sections.map((section, idx) => (
                <div key={section.title} data-testid={`terms-section-${idx + 1}`}>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                    {section.title}
                  </h2>
                  {section.intro && (
                    <p className="text-gray-700 leading-relaxed mb-3">{section.intro}</p>
                  )}
                  {section.body && (
                    <p className="text-gray-700 leading-relaxed">{section.body}</p>
                  )}
                  {section.items && (
                    <ul className="space-y-2 text-gray-700 list-disc pl-6">
                      {section.items.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {section.outro && (
                    <p className="text-gray-700 leading-relaxed mt-3">{section.outro}</p>
                  )}
                </div>
              ))}

              {/* Contact */}
              <div className="pt-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">
                  20. Contact Us
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  For any questions or clarifications regarding these Terms of Service, please reach out to us:
                </p>
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-5 sm:p-6 space-y-3">
                  <div className="font-semibold text-gray-900">{company.name}</div>
                  <div className="flex items-start gap-3 text-gray-700">
                    <MapPin className="h-5 w-5 text-cyan-600 mt-0.5 flex-shrink-0" />
                    <span>{company.address}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Phone className="h-5 w-5 text-cyan-600 flex-shrink-0" />
                    <span>{company.phone} / {company.phone2}</span>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Mail className="h-5 w-5 text-cyan-600 flex-shrink-0" />
                    <a href={`mailto:${company.email}`} className="text-cyan-700 hover:text-cyan-800 underline underline-offset-2">
                      {company.email}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermsOfService;
